import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Plus, Edit, Trash2, Save, Image as ImageIcon, Film, Eye, EyeOff, X, ChevronLeft } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from 'sonner';
import apiClient from '@/utils/api';
import ImageUpload from '@/components/ImageUpload';

export default function AdminLibrary() {
  const [locations, setLocations] = useState([]);
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [media, setMedia] = useState([]);
  const [isLocationModalOpen, setIsLocationModalOpen] = useState(false);
  const [isMediaModalOpen, setIsMediaModalOpen] = useState(false);
  const [editingLocation, setEditingLocation] = useState(null);
  const [editingMedia, setEditingMedia] = useState(null);
  const [locationForm, setLocationForm] = useState({ name: '', slug: '', description: '', cover_image: '', order: 0, is_active: true });
  const [mediaForm, setMediaForm] = useState({ media_type: 'image', url: '', caption: '', order: 0 });

  useEffect(() => { fetchLocations(); }, []);

  const fetchLocations = async () => {
    try {
      const res = await apiClient.get('/admin/library/locations');
      setLocations(res.data);
    } catch (e) { console.error(e); }
  };

  const fetchMedia = async (locationId) => {
    try {
      const res = await apiClient.get(`/admin/library/locations/${locationId}/media`);
      setMedia(res.data);
    } catch (e) { console.error(e); }
  };

  const generateSlug = (name) => name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');

  const handleLocationSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...locationForm, slug: locationForm.slug || generateSlug(locationForm.name) };
      if (editingLocation) {
        await apiClient.put(`/admin/library/locations/${editingLocation.id}`, payload);
        toast.success('Location updated!');
      } else {
        await apiClient.post('/admin/library/locations', payload);
        toast.success('Location created!');
      }
      setIsLocationModalOpen(false);
      resetLocationForm();
      fetchLocations();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save location');
    }
  };

  const handleMediaSubmit = async (e) => {
    e.preventDefault();
    if (!mediaForm.url) { toast.error('URL is required'); return; }
    try {
      const payload = { ...mediaForm, location_id: selectedLocation.id };
      if (editingMedia) {
        await apiClient.put(`/admin/library/media/${editingMedia.id}`, payload);
        toast.success('Media updated!');
      } else {
        await apiClient.post('/admin/library/media', payload);
        toast.success('Media added!');
      }
      setIsMediaModalOpen(false);
      resetMediaForm();
      fetchMedia(selectedLocation.id);
      fetchLocations();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save media');
    }
  };

  const handleDeleteLocation = async (id) => {
    if (!window.confirm('Delete this location and all its media?')) return;
    try {
      await apiClient.delete(`/admin/library/locations/${id}`);
      toast.success('Location deleted!');
      if (selectedLocation?.id === id) setSelectedLocation(null);
      fetchLocations();
    } catch (e) { toast.error('Failed to delete'); }
  };

  const handleDeleteMedia = async (id) => {
    if (!window.confirm('Delete this media item?')) return;
    try {
      await apiClient.delete(`/admin/library/media/${id}`);
      toast.success('Media deleted!');
      fetchMedia(selectedLocation.id);
      fetchLocations();
    } catch (e) { toast.error('Failed to delete'); }
  };

  const resetLocationForm = () => { setLocationForm({ name: '', slug: '', description: '', cover_image: '', order: 0, is_active: true }); setEditingLocation(null); };
  const resetMediaForm = () => { setMediaForm({ media_type: 'image', url: '', caption: '', order: 0 }); setEditingMedia(null); };

  const openEditLocation = (loc) => {
    setEditingLocation(loc);
    setLocationForm({ name: loc.name, slug: loc.slug, description: loc.description || '', cover_image: loc.cover_image || '', order: loc.order, is_active: loc.is_active });
    setIsLocationModalOpen(true);
  };

  const openEditMedia = (item) => {
    setEditingMedia(item);
    setMediaForm({ media_type: item.media_type, url: item.url, caption: item.caption || '', order: item.order });
    setIsMediaModalOpen(true);
  };

  const openLocation = (loc) => {
    setSelectedLocation(loc);
    fetchMedia(loc.id);
  };

  // Location List View
  if (!selectedLocation) {
    return (
      <div className="min-h-screen bg-gray-100">
        <div className="bg-white shadow">
          <div className="max-w-7xl mx-auto px-4 py-6">
            <Link to="/admin" className="inline-flex items-center text-gray-600 hover:text-gray-900 mb-4">
              <ArrowLeft size={20} className="mr-2" /> Back to Dashboard
            </Link>
            <div className="flex justify-between items-center">
              <div>
                <h1 className="text-3xl font-bold text-gray-900">Library</h1>
                <p className="text-gray-600 mt-1">Manage gallery locations, images & videos</p>
              </div>
              <Button onClick={() => { resetLocationForm(); setIsLocationModalOpen(true); }} className="bg-green-600 hover:bg-green-700" data-testid="add-location-btn">
                <Plus size={18} className="mr-2" /> Add Location
              </Button>
            </div>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-4 py-8">
          {locations.length === 0 ? (
            <div className="bg-white rounded-lg shadow p-12 text-center">
              <ImageIcon size={48} className="mx-auto mb-4 text-gray-300" />
              <p className="text-gray-500 text-lg">No gallery locations yet. Add your first location.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {locations.map((loc) => (
                <div key={loc.id} className="bg-white rounded-lg shadow-md overflow-hidden" data-testid={`library-loc-${loc.slug}`}>
                  <div className="relative h-44 bg-gray-200 cursor-pointer" onClick={() => openLocation(loc)}>
                    {loc.cover_image ? (
                      <img src={loc.cover_image} alt={loc.name} className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center"><ImageIcon size={40} className="text-gray-400" /></div>
                    )}
                    {!loc.is_active && (
                      <div className="absolute top-2 left-2 bg-red-500 text-white text-xs px-2 py-1 rounded">Hidden</div>
                    )}
                    <div className="absolute bottom-2 right-2 bg-black/60 text-white text-xs px-2 py-1 rounded">
                      {loc.media_count || 0} items
                    </div>
                  </div>
                  <div className="p-4">
                    <h3 className="font-semibold text-lg text-gray-900 mb-1">{loc.name}</h3>
                    <p className="text-sm text-gray-500 mb-3 line-clamp-1">{loc.description || 'No description'}</p>
                    <div className="flex gap-2">
                      <Button size="sm" onClick={() => openLocation(loc)} className="bg-blue-600 hover:bg-blue-700 flex-1">
                        <Eye size={14} className="mr-1" /> Manage Media
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => openEditLocation(loc)}>
                        <Edit size={14} />
                      </Button>
                      <Button size="sm" variant="destructive" onClick={() => handleDeleteLocation(loc.id)}>
                        <Trash2 size={14} />
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Location Modal */}
        <Dialog open={isLocationModalOpen} onOpenChange={(open) => { if (!open) resetLocationForm(); setIsLocationModalOpen(open); }}>
          <DialogContent className="max-w-lg">
            <DialogHeader><DialogTitle>{editingLocation ? 'Edit Location' : 'Add Location'}</DialogTitle></DialogHeader>
            <form onSubmit={handleLocationSubmit} className="space-y-4">
              <div>
                <Label>Location Name *</Label>
                <Input value={locationForm.name} onChange={e => setLocationForm({ ...locationForm, name: e.target.value, slug: generateSlug(e.target.value) })} placeholder="e.g., C1 - Sarjapur Road" required />
              </div>
              <div>
                <Label>Slug</Label>
                <Input value={locationForm.slug} onChange={e => setLocationForm({ ...locationForm, slug: e.target.value })} placeholder="auto-generated" />
              </div>
              <div>
                <Label>Description</Label>
                <Textarea value={locationForm.description} onChange={e => setLocationForm({ ...locationForm, description: e.target.value })} placeholder="Brief description of this location" />
              </div>
              <ImageUpload label="Cover Image" value={locationForm.cover_image} onChange={url => setLocationForm({ ...locationForm, cover_image: url })} />
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Display Order</Label>
                  <Input type="number" value={locationForm.order} onChange={e => setLocationForm({ ...locationForm, order: parseInt(e.target.value) || 0 })} />
                </div>
                <div className="flex items-end pb-2">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input type="checkbox" checked={locationForm.is_active} onChange={e => setLocationForm({ ...locationForm, is_active: e.target.checked })} className="w-4 h-4" />
                    <span className="text-sm font-medium">Active (visible on website)</span>
                  </label>
                </div>
              </div>
              <div className="flex justify-end gap-2">
                <Button type="button" variant="outline" onClick={() => { resetLocationForm(); setIsLocationModalOpen(false); }}>Cancel</Button>
                <Button type="submit" className="bg-blue-600 hover:bg-blue-700"><Save size={16} className="mr-2" /> Save</Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>
    );
  }

  // Media Management View
  return (
    <div className="min-h-screen bg-gray-100">
      <div className="bg-white shadow">
        <div className="max-w-7xl mx-auto px-4 py-6">
          <button onClick={() => setSelectedLocation(null)} className="inline-flex items-center text-gray-600 hover:text-gray-900 mb-4">
            <ChevronLeft size={20} className="mr-1" /> Back to Locations
          </button>
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">{selectedLocation.name}</h1>
              <p className="text-gray-600 mt-1">Manage images & videos for this location</p>
            </div>
            <Button onClick={() => { resetMediaForm(); setIsMediaModalOpen(true); }} className="bg-green-600 hover:bg-green-700" data-testid="add-media-btn">
              <Plus size={18} className="mr-2" /> Add Media
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-8">
        {media.length === 0 ? (
          <div className="bg-white rounded-lg shadow p-12 text-center">
            <Film size={48} className="mx-auto mb-4 text-gray-300" />
            <p className="text-gray-500 text-lg mb-4">No media yet. Add images or videos.</p>
            <Button onClick={() => { resetMediaForm(); setIsMediaModalOpen(true); }} className="bg-green-600 hover:bg-green-700">
              <Plus size={18} className="mr-2" /> Add First Media
            </Button>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {media.map((item) => (
              <div key={item.id} className="bg-white rounded-lg shadow overflow-hidden" data-testid={`media-card-${item.id}`}>
                <div className="relative h-40 bg-gray-200">
                  {item.media_type === 'video' ? (
                    <>
                      {item.url.includes('youtube.com') || item.url.includes('youtu.be') ? (
                        <img
                          src={`https://img.youtube.com/vi/${item.url.match(/(?:youtube\.com\/(?:watch\?v=|embed\/)|youtu\.be\/)([a-zA-Z0-9_-]+)/)?.[1]}/hqdefault.jpg`}
                          alt="Video thumbnail"
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <video src={item.url} className="w-full h-full object-cover" muted />
                      )}
                      <div className="absolute inset-0 flex items-center justify-center">
                        <div className="w-10 h-10 rounded-full bg-[#E12429]/80 flex items-center justify-center">
                          <Film size={18} className="text-white" />
                        </div>
                      </div>
                    </>
                  ) : (
                    <img src={item.url} alt={item.caption || ''} className="w-full h-full object-cover" />
                  )}
                  <span className={`absolute top-2 left-2 text-xs px-2 py-1 rounded font-medium ${item.media_type === 'video' ? 'bg-red-500 text-white' : 'bg-white text-gray-700'}`}>
                    {item.media_type}
                  </span>
                </div>
                <div className="p-3">
                  <p className="text-sm text-gray-700 mb-2 truncate">{item.caption || 'No caption'}</p>
                  <p className="text-xs text-gray-400 mb-2 truncate">{item.url}</p>
                  <div className="flex gap-2">
                    <Button size="sm" variant="outline" onClick={() => openEditMedia(item)} className="flex-1">
                      <Edit size={14} className="mr-1" /> Edit
                    </Button>
                    <Button size="sm" variant="destructive" onClick={() => handleDeleteMedia(item.id)}>
                      <Trash2 size={14} />
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Media Modal */}
      <Dialog open={isMediaModalOpen} onOpenChange={(open) => { if (!open) resetMediaForm(); setIsMediaModalOpen(open); }}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>{editingMedia ? 'Edit Media' : 'Add Media'}</DialogTitle></DialogHeader>
          <form onSubmit={handleMediaSubmit} className="space-y-4">
            <div>
              <Label>Media Type</Label>
              <div className="flex gap-3 mt-1">
                {['image', 'video'].map(type => (
                  <button
                    key={type}
                    type="button"
                    onClick={() => setMediaForm({ ...mediaForm, media_type: type })}
                    className={`flex items-center gap-2 px-4 py-2 rounded-lg border-2 transition-colors ${
                      mediaForm.media_type === type ? 'border-[#E12429] bg-red-50 text-[#E12429]' : 'border-gray-200 text-gray-600 hover:border-gray-300'
                    }`}
                  >
                    {type === 'image' ? <ImageIcon size={16} /> : <Film size={16} />}
                    <span className="capitalize font-medium">{type}</span>
                  </button>
                ))}
              </div>
            </div>
            {mediaForm.media_type === 'image' ? (
              <ImageUpload label="Image" value={mediaForm.url} onChange={url => setMediaForm({ ...mediaForm, url })} required />
            ) : (
              <div>
                <Label>Video URL *</Label>
                <Input value={mediaForm.url} onChange={e => setMediaForm({ ...mediaForm, url: e.target.value })} placeholder="YouTube link or direct video URL (.mp4)" required />
                <p className="text-xs text-gray-500 mt-1">Supports YouTube links, Vimeo, or direct video file URLs</p>
              </div>
            )}
            <div>
              <Label>Caption</Label>
              <Input value={mediaForm.caption} onChange={e => setMediaForm({ ...mediaForm, caption: e.target.value })} placeholder="Describe this media" />
            </div>
            <div>
              <Label>Display Order</Label>
              <Input type="number" value={mediaForm.order} onChange={e => setMediaForm({ ...mediaForm, order: parseInt(e.target.value) || 0 })} />
            </div>
            <div className="flex justify-end gap-2">
              <Button type="button" variant="outline" onClick={() => { resetMediaForm(); setIsMediaModalOpen(false); }}>Cancel</Button>
              <Button type="submit" className="bg-blue-600 hover:bg-blue-700"><Save size={16} className="mr-2" /> Save</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
